<template>
  <template v-if="type === DealIDType.External || type === DealIDType.VideoDisplay">
    <deal-i-d
      :deal-i-d="dealId"
      :small="true"
      :show-copy-button="false"
      :show-icon="true" />
  </template>

  <expandable-card-template
    v-else
    ref="expandableCardRef"
    :is-loading="isLoading">
    <template #header>
      <p
        v-tooltip:clever="dealName"
        class="name"
        :title="dealName">
        <router-link
          :to="deal.endpoint"
          target="_blank">
          {{ dealName }}
        </router-link>
      </p>
    </template>
    <template #details>
      <div class="logo">
        <img
          v-if="logoUrl"
          :src="logoUrl" />
        <p v-else>{{ mediaOwnerName }}</p>
      </div>
      <deal-i-d
        :deal-i-d="dealId"
        :small="true"
        :show-copy-button="false"
        :show-icon="true" />
    </template>
    <template #expanded-content>
      <div
        v-for="(item, i) in displayItems"
        :key="i"
        class="item">
        <p class="label">{{ item.label }}</p>
        <p
          v-tooltip:clever="item.value"
          class="value">
          {{ item.value }}
        </p>
      </div>
    </template>
  </expandable-card-template>
</template>

<script lang="ts" setup>
  import { capitalize, computed, onMounted, ref, watch } from 'vue';

  import DealID from '@/common/components/deals/DealID.vue';
  import ExpandableCardTemplate from '@/common/components/templates/ExpandableCardTemplate.vue';
  import { Organization, Package } from '@/common/models';
  import MediaOwner from '@/common/models/media-owner/MediaOwner';
  import DealIDType from '@/common/models/package/DealIDType.enum';
  import { getValueOrDash } from '@/globals';
  import { useMediaOwners } from '@/org-app/services/tanstack/queries';
  import { waitForPendingQueries } from '@/org-app/services/tanstack/queryUtils';
  import { waitUntil } from '@/utils/DelayHelper';
  import { mapOrgAppActions, mapOrgAppState } from '@/utils/StateMapping';
  import i18n from '@/vueI18n';

  interface IProps {
    deal: Package;
    packageId: number;
    type: DealIDType;
    hiddenDealCount?: number;
    defaultExpanded?: boolean;
  }

  const props = withDefaults(defineProps<IProps>(), {
    hiddenDealCount: 0,
    defaultExpanded: false,
  });

  const isLoading = ref<boolean>(false);
  const expandableCardRef = ref<InstanceType<typeof ExpandableCardTemplate> | null>(null);
  const mediaOwnersQuery = useMediaOwners();

  const { getCurators, getDemographics } = mapOrgAppActions();
  const { ReferenceData } = mapOrgAppState();

  const demographics = computed(() => ReferenceData.demographics);
  const dealName = computed<string>(() => getValueOrDash(props.deal?.name));
  const dealId = computed<string>(() => getValueOrDash(props.deal?.dealID.toString()));
  const curators = computed<Organization[]>(() => ReferenceData.curators);
  const curator = computed<Organization>(() => curators.value.find((x) => x.id === props.deal?.organizationID));
  const mediaOwner = computed<MediaOwner>(() =>
    mediaOwnersQuery.items.value.find((x) => x.id === props.deal?.organizationID),
  );
  const mediaOwnerName = computed<string>(() => getValueOrDash(mediaOwner.value?.name));
  const logoUrl = computed<string>(() => {
    if (props.type === DealIDType.Curated) {
      return curator.value?.logoUrl;
    }

    return mediaOwner.value?.logoUrl;
  });
  const demographicDescription = computed<string>(() => {
    if (props.deal?.demographicTargetIDs?.length > 0) {
      return props.deal.demographicTargetIDs
        .map((id) => demographics.value.find((d) => d.id === id)?.name)
        .filter((x) => x)
        .join(', ');
    }

    return capitalize(i18n.global.t('messages.allAudiences'));
  });
  const displayItems = computed(() => {
    if (!props.deal && !expandableCardRef.value.isExpanded) return [];

    if (props.type === DealIDType.Curated) {
      return [
        {
          label: i18n.global.t('messages.dailyImpressions'),
          value: getValueOrDash(props.deal.totalCuratedDeliveryStatistics?.dailyImpressionsFormatted),
        },
        {
          label: i18n.global.t('names.eCPM'),
          value: props.deal.totalCuratedCPMFormatted,
        },
        {
          label: i18n.global.t('messages.siteCount'),
          value: getValueOrDash(props.deal.totalCuratedDeliveryStatistics?.siteCountFormatted),
        },
        {
          label: i18n.global.t('names.screenCount'),
          value: getValueOrDash(props.deal.totalCuratedDeliveryStatistics?.unitCountFormatted),
        },
        {
          label: i18n.global.t('names.date', { n: 2 }),
          value: props.deal.datesDescription,
        },
      ];
    }

    return [
      {
        label: i18n.global.t('names.purchaseType'),
        value: props.deal.purchaseTypeName,
      },
      {
        label: i18n.global.t('names.budget'),
        value: props.deal.budgetDescription,
      },
      {
        label: i18n.global.t('names.impression', { n: 2 }),
        value: props.deal.impressionsDescription,
      },
      {
        label: i18n.global.t('names.cpm'),
        value: props.deal.cpmDescription,
      },
      {
        label: i18n.global.t('names.network', { n: 2 }),
        value: props.deal.networksDescription,
      },
      {
        label: i18n.global.t('names.location', { n: 2 }),
        value: props.deal.targetingDescriptionShort,
      },
      {
        label: i18n.global.t('names.date', { n: 2 }),
        value: props.deal.datesDescription,
      },
      {
        label: i18n.global.t('names.audience', { n: 2 }),
        value: demographicDescription.value,
      },
    ];
  });

  async function toggleCard(value: boolean = null): Promise<void> {
    await waitUntil(() => !!expandableCardRef.value);
    expandableCardRef.value.toggleCard(value);
  }

  watch(
    () => props.defaultExpanded,
    () => {
      toggleCard(props.defaultExpanded);
    },
  );

  onMounted(async () => {
    await waitForPendingQueries([mediaOwnersQuery]);

    if (props.type === DealIDType.Curated && curators.value?.length === 0) {
      await getCurators({});
    }

    if (props.deal.demographicTargetIDs?.length && demographics.value?.length === 0) {
      await getDemographics({});
    }
  });

  defineExpose({
    toggleCard,
  });
</script>

<style scoped lang="scss">
  @use '@/styles/variables' as *;

  .expandable-card {
    p {
      margin: 0;
      white-space: nowrap;
      text-overflow: ellipsis;
      overflow: hidden;
    }

    .logo {
      padding: 1rem;
      border-radius: 0.3rem;
      display: flex;
      justify-content: center;

      img {
        max-height: 2rem;
      }

      p {
        font-weight: 700;
        text-align: center;
      }
    }

    .deal-id {
      padding: 0 1rem 1.5rem 1rem;
    }

    .expandable {
      margin-bottom: 2rem;
      animation: expand 0.5s ease;

      .item {
        display: grid;
        grid-template-columns: 1fr 1.5fr;
        padding: 0.5rem 1rem;

        &:nth-of-type(odd) {
          background-color: var(--paneHeaderBackgroundColor);
        }

        .label {
          color: var(--textMutedColor);
          text-align: center;
          text-transform: uppercase;
          font-weight: 400;
          font-size: 1.1rem;
          display: flex;
          align-items: center;
        }

        .value {
          font-weight: 700;
          text-align: center;
          font-size: 1.1rem;
        }
      }
    }
  }
</style>
